/**
 * Rascals — agent surface. Per-client rascals + staff outsiders in one
 * unified card grid, each card tagged with its kind. Rascal cards open the
 * rascal workspace (/rascals/:handle); outsider cards open the outsider
 * workspace (/agents/:handle).
 *
 * (Outsiders were folded into this surface 2026-06-15. The separate
 * "Employee Agents" surface is reserved for a future agent type.)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  RefreshCw,
  Plus,
  X,
} from 'lucide-react';
import { PageLoader } from '../components/LoadingSpinner';
import { EmptyState } from '../components/EmptyState';

type AgentKind = 'rascal' | 'outsider';

interface Agent {
  kind: AgentKind;
  tenantId: string;
  handle: string;
  displayName: string;
  cli: 'claude' | 'ollama' | 'codex';
  client: string;
  projectDir: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

const KIND_LABEL: Record<AgentKind, string> = { rascal: 'Client manager', outsider: 'Staff' };
const KIND_HUE: Record<AgentKind, string> = { rascal: '#b56cff', outsider: '#4df5a5' };

const DESK_POSITIONS = [
  { left: '8.75%', top: '63%', depth: 12, tone: 'light' },
  { left: '19.75%', top: '63%', depth: 12, tone: 'light' },
  { left: '19.75%', top: '39%', depth: 10, tone: 'dark' },
  { left: '30.75%', top: '39%', depth: 10, tone: 'dark' },
  { left: '25.25%', top: '22%', depth: 8, tone: 'dark' },
  { left: '36.25%', top: '22%', depth: 8, tone: 'dark' },
  { left: '79%', top: '63%', depth: 12, tone: 'light' },
  { left: '90%', top: '63%', depth: 12, tone: 'light' },
  { left: '68%', top: '39%', depth: 10, tone: 'dark' },
  { left: '79%', top: '39%', depth: 10, tone: 'dark' },
  { left: '65.25%', top: '22%', depth: 8, tone: 'dark' },
  { left: '73.5%', top: '22%', depth: 8, tone: 'dark' },
];

const WALL_OFFICE_POSITIONS = [
  { left: '7%', top: '21%' },
  { left: '19%', top: '12%' },
  { left: '36%', top: '11%' },
  { left: '52%', top: '10%' },
  { left: '69%', top: '11%' },
  { left: '84%', top: '12%' },
  { left: '94%', top: '21%' },
];

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('boss_token') ?? '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchAgents(): Promise<Agent[]> {
  const [rRes, oRes] = await Promise.all([
    fetch('api/agents/rascals', { headers: authHeaders() }),
    fetch('api/agents/outsiders', { headers: authHeaders() }),
  ]);
  if (!rRes.ok) throw new Error(`rascals HTTP ${rRes.status}`);
  if (!oRes.ok) throw new Error(`outsiders HTTP ${oRes.status}`);
  const rBody = (await rRes.json()) as { rascals: Omit<Agent, 'kind'>[] };
  const oBody = (await oRes.json()) as { outsiders: Omit<Agent, 'kind'>[] };
  const rascals: Agent[] = (rBody.rascals ?? []).map((a) => ({ ...a, kind: 'rascal' as const }));
  const outsiders: Agent[] = (oBody.outsiders ?? []).map((a) => ({ ...a, kind: 'outsider' as const }));
  // Dedupe by kind:handle (boss_outsiders can carry cross-tenant dupes).
  const seen = new Set<string>();
  const merged: Agent[] = [];
  for (const a of [...rascals, ...outsiders]) {
    const k = `${a.kind}:${a.handle}`;
    if (seen.has(k)) continue;
    seen.add(k);
    merged.push(a);
  }
  merged.sort((a, b) => a.displayName.localeCompare(b.displayName));
  return merged;
}

interface CreateBody {
  handle: string;
  displayName: string;
  cli: 'claude' | 'ollama' | 'codex';
  client: string;
  projectDir: string;
  enabled: boolean;
}

async function createAgent(kind: AgentKind, body: CreateBody): Promise<void> {
  const base = kind === 'rascal' ? 'rascals' : 'outsiders';
  const res = await fetch(`api/agents/${base}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { message?: string }).message ?? `HTTP ${res.status}`);
  }
}

interface DeskTagProps {
  agent: Agent;
  index: number;
}

function DeskTag({ agent, index }: DeskTagProps) {
  const position = DESK_POSITIONS[index];
  const to = agent.kind === 'rascal' ? `/rascals/${agent.handle}` : `/agents/${agent.handle}`;
  const darkTag = position.tone === 'dark';
  const highContrastTag = agent.displayName === 'Buckwheat Magnussen' || agent.displayName === 'Porky Trusted';
  return (
    <Link
      to={to}
      className={`group absolute z-20 flex min-w-[88px] -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-bold shadow-2xl ring-1 backdrop-blur-sm transition hover:-translate-y-[calc(50%+2px)] focus:outline-none focus:ring-2 focus:ring-emerald-200 sm:min-w-[96px] sm:text-[12px] ${
        highContrastTag
          ? 'border-[#ffe7a3]/95 bg-[#07140f]/96 text-white ring-[#ffe7a3]/35 hover:bg-[#0d2118]'
          : darkTag
            ? 'border-[#b7e2c8]/80 bg-[#10251f]/92 text-[#fff9e8] ring-white/15 hover:bg-[#18352d]'
            : 'border-[#f3d99b] bg-[#fff5d6]/96 text-[#14201c] ring-black/10 hover:bg-white'
      }`}
      style={{
        left: position.left,
        top: position.top,
        boxShadow: highContrastTag
          ? '0 12px 28px rgba(0, 0, 0, 0.52), 0 0 0 1px rgba(255, 231, 163, 0.22), inset 0 1px 0 rgba(255,255,255,0.22)'
          : darkTag
            ? '0 10px 26px rgba(0, 0, 0, 0.42), inset 0 1px 0 rgba(255,255,255,0.18)'
            : '0 10px 26px rgba(12, 16, 14, 0.35), inset 0 1px 0 rgba(255,255,255,0.72)',
        textShadow: highContrastTag ? '0 1px 2px rgba(0,0,0,0.88)' : undefined,
        zIndex: position.depth,
      }}
      data-testid="agent-card"
      title={`${agent.displayName} · ${agent.client || agent.handle}`}
    >
      <span className={`h-2 w-2 flex-shrink-0 rounded-full ring-1 ring-white/70 ${agent.enabled ? 'bg-emerald-500' : 'bg-zinc-400'}`} aria-hidden />
      <span className="max-w-[124px] truncate sm:max-w-[168px]">{agent.displayName}</span>
    </Link>
  );
}

interface OverflowDockProps {
  title: string;
  agents: Agent[];
}

function OverflowDock({ title, agents }: OverflowDockProps) {
  if (agents.length === 0) return null;
  return (
    <div className="rounded-lg border border-white/35 bg-white/55 p-3 text-[#15201d] shadow-xl backdrop-blur-xl">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#53655d]">{title}</div>
      <div className="flex flex-wrap gap-2">
        {agents.map((agent) => (
          <Link
            key={`${agent.kind}:${agent.handle}`}
            to={agent.kind === 'rascal' ? `/rascals/${agent.handle}` : `/agents/${agent.handle}`}
            className="rounded-md border border-[#1b2a26]/14 bg-white/76 px-2.5 py-1.5 text-[11px] font-semibold text-[#15201d] shadow-sm transition hover:bg-white"
            title={`${agent.displayName} · ${agent.client || agent.handle}`}
          >
            {agent.displayName}
          </Link>
        ))}
      </div>
    </div>
  );
}

interface WallOfficeTagProps {
  agent: Agent;
  index: number;
}

function WallOfficeTag({ agent, index }: WallOfficeTagProps) {
  const position = WALL_OFFICE_POSITIONS[index];
  return (
    <Link
      to={`/agents/${agent.handle}`}
      className="group absolute z-20 flex min-w-[90px] -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-1.5 rounded-md border border-[#b7e2c8]/80 bg-[#12251f]/90 px-2.5 py-1.5 text-[10.5px] font-bold text-[#fff9e8] shadow-2xl ring-1 ring-white/15 backdrop-blur-md transition hover:-translate-y-[calc(50%+2px)] hover:bg-[#18352d] focus:outline-none focus:ring-2 focus:ring-amber-200"
      style={{
        left: position.left,
        top: position.top,
        boxShadow: '0 12px 30px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.18)',
      }}
      title={`${agent.displayName} · ${agent.client || agent.handle}`}
    >
      <span className={`h-2 w-2 flex-shrink-0 rounded-full ring-1 ring-white/50 ${agent.enabled ? 'bg-emerald-400' : 'bg-zinc-400'}`} aria-hidden />
      <span className="max-w-[142px] truncate">{agent.displayName}</span>
    </Link>
  );
}

interface CreateModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreateModal({ open, onClose, onCreated }: CreateModalProps) {
  const [kind, setKind] = useState<AgentKind>('rascal');
  const [form, setForm] = useState<CreateBody>({
    handle: '',
    displayName: '',
    cli: 'claude',
    client: '',
    projectDir: '',
    enabled: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setKind('rascal');
      setForm({ handle: '', displayName: '', cli: 'claude', client: '', projectDir: '', enabled: true });
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleValid = /^[a-z]{2,24}$/.test(form.handle);
  const valid = handleValid && form.displayName.trim().length > 0 && form.projectDir.trim().length > 0;

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      await createAgent(kind, form);
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  }

  const inputBase = 'w-full bg-surface-2/70 border border-border rounded-md px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent/60';

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-6" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-xl border border-border bg-surface-1/95 p-5 shadow-xl">
        <header className="flex items-start justify-between mb-4">
          <div>
            <div className="vs-mono text-[10px] uppercase tracking-[0.22em] text-text-muted">Action</div>
            <h2 className="text-lg font-semibold mt-1">Add client manager</h2>
          </div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] vs-mono uppercase tracking-wider text-text-muted mb-1">Type</label>
            <div className="grid grid-cols-2 gap-2">
              {(['rascal', 'outsider'] as AgentKind[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={`px-3 py-2 rounded-md text-[12px] border transition-colors ${
                    kind === k ? 'text-text-primary' : 'bg-surface-2 text-text-muted border-border hover:text-text-primary'
                  }`}
                  style={kind === k ? { borderColor: `${KIND_HUE[k]}88`, background: `${KIND_HUE[k]}18`, color: KIND_HUE[k] } : undefined}
                >
                  {KIND_LABEL[k]}{k === 'rascal' ? ' (client manager)' : ' (staff)'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] vs-mono uppercase tracking-wider text-text-muted mb-1">Handle</label>
            <input
              autoFocus
              className={`${inputBase} font-mono`}
              placeholder="lowercase a–z, 2–24 chars (e.g. support)"
              value={form.handle}
              onChange={(e) => setForm((f) => ({ ...f, handle: e.target.value.trim() }))}
            />
            {!handleValid && form.handle.length > 0 && (
              <p className="text-[10.5px] text-danger mt-1">Must be lowercase letters only, 2–24 chars.</p>
            )}
          </div>
          <div>
            <label className="block text-[11px] vs-mono uppercase tracking-wider text-text-muted mb-1">Display name</label>
            <input
              className={inputBase}
              placeholder="e.g. Customer Service"
              value={form.displayName}
              onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-[11px] vs-mono uppercase tracking-wider text-text-muted mb-1">Client</label>
            <input
              className={inputBase}
              placeholder={kind === 'rascal' ? 'e.g. Acme Corp' : 'e.g. staff function'}
              value={form.client}
              onChange={(e) => setForm((f) => ({ ...f, client: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-[11px] vs-mono uppercase tracking-wider text-text-muted mb-1">Project dir</label>
            <input
              className={`${inputBase} font-mono`}
              placeholder={kind === 'rascal' ? '/home/tcntryprd/rascals/<handle>' : '/home/tcntryprd/outsiders/<handle>'}
              value={form.projectDir}
              onChange={(e) => setForm((f) => ({ ...f, projectDir: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] vs-mono uppercase tracking-wider text-text-muted mb-1">CLI</label>
              <select
                className={inputBase}
                value={form.cli}
                onChange={(e) => setForm((f) => ({ ...f, cli: e.target.value as 'claude' | 'ollama' | 'codex' }))}
              >
                <option value="claude">claude</option>
                <option value="ollama">ollama</option>
                <option value="codex">codex</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] vs-mono uppercase tracking-wider text-text-muted mb-1">Enabled</label>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, enabled: !f.enabled }))}
                className={`w-full px-3 py-2 rounded-md text-sm border ${form.enabled ? 'bg-success/15 text-success border-success/40' : 'bg-surface-2 text-text-muted border-border'}`}
              >
                {form.enabled ? 'enabled' : 'disabled'}
              </button>
            </div>
          </div>
          {error && <p className="text-[12px] text-danger">{error}</p>}
        </div>
        <footer className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-border">
          <button type="button" onClick={onClose} className="text-[12px] text-text-muted hover:text-text-primary px-3 py-1.5">Cancel</button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!valid || submitting}
            className="text-[12px] font-semibold text-[#0a0c12] px-4 py-1.5 rounded-md disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #b56cff 0%, #5cc8ff 100%)' }}
          >
            {submitting ? 'Creating…' : `Create ${KIND_LABEL[kind].toLowerCase()}`}
          </button>
        </footer>
      </div>
    </div>
  );
}

export function Rascals() {
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setAgents(await fetchAgents());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (agents === null && loading) {
    return <PageLoader />;
  }

  const rascalCount = (agents ?? []).filter((a) => a.kind === 'rascal').length;
  const staffCount = (agents ?? []).filter((a) => a.kind === 'outsider').length;
  const rascals = (agents ?? []).filter((a) => a.kind === 'rascal');
  const outsiders = (agents ?? []).filter((a) => a.kind === 'outsider');
  const floorRascals = rascals.slice(0, DESK_POSITIONS.length);
  const extraRascals = rascals.slice(DESK_POSITIONS.length);
  const wallOffices = outsiders.slice(0, WALL_OFFICE_POSITIONS.length);
  const extraOutsiders = outsiders.slice(WALL_OFFICE_POSITIONS.length);

  return (
    <div className="relative h-full min-h-[calc(100vh-2.5rem)] overflow-hidden px-4 py-4 text-white sm:px-5" data-testid="rascals-page">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/14 via-transparent to-black/8" aria-hidden />

      <div className="relative z-10 h-full">
        <div className="absolute right-3 top-3 z-30 flex items-center gap-2 rounded-full border border-white/24 bg-[#101815]/44 p-1.5 text-[#fff9e8] shadow-2xl backdrop-blur-md">
          <div
            className="hidden items-center gap-1.5 rounded-full border border-white/14 bg-white/14 px-3 py-2 text-[11px] font-semibold text-white/88 sm:flex"
            title={`${staffCount} on-demand Phoenix specialists, ${rascalCount} assigned client desks`}
          >
            {staffCount} on-demand specialists · {rascalCount} assigned client desks
          </div>
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={loading}
            className="grid h-9 w-9 place-items-center rounded-full border border-white/18 bg-white/16 text-white shadow-sm transition hover:bg-white/28 disabled:opacity-50"
            aria-label="Refresh Phoenix specialists"
            title="Refresh Phoenix specialists"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="grid h-9 w-9 place-items-center rounded-full text-[#0a0c12] shadow-lg"
            style={{ background: 'linear-gradient(135deg, #f5d77b 0%, #5df0ae 100%)' }}
            aria-label="Add assigned client manager"
            title="Add assigned client manager"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        {error && (
          <div className="absolute left-0 right-0 top-3 z-40 rounded-md border border-red-300/50 bg-red-100/82 p-3 text-[12px] font-medium text-red-900 shadow-xl backdrop-blur-md">
            {error}
          </div>
        )}

        <div className="absolute inset-0">
          {agents && agents.length === 0 ? (
            <div className="absolute left-1/2 top-1/2 w-[min(520px,calc(100%-32px))] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-white/34 bg-white/68 p-6 text-[#15201d] shadow-xl backdrop-blur-md">
              <EmptyState
                title="No client managers yet"
                description="Add a client manager to get started — give them a handle, a display name, and a role."
              />
            </div>
          ) : (
            <>
              {floorRascals.map((agent, index) => (
                <DeskTag key={`${agent.kind}:${agent.handle}`} agent={agent} index={index} />
              ))}
              {wallOffices.map((agent, index) => (
                <WallOfficeTag key={`${agent.kind}:${agent.handle}`} agent={agent} index={index} />
              ))}

              <div className="absolute inset-x-0 bottom-0 grid gap-3 lg:grid-cols-2">
                <OverflowDock title="Extra client manager desks" agents={extraRascals} />
                <OverflowDock title="Additional wall offices" agents={extraOutsiders} />
              </div>
            </>
          )}
        </div>
      </div>

      <CreateModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => void refresh()} />
    </div>
  );
}
